import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { LoginService } from 'src/app/services/login.service';
import { InitialConfigService } from 'src/app/services/inital-config.service';
import { ToastService } from 'src/app/services/toast.service';
import { Router } from '@angular/router';
import { Environment } from 'src/environments/environment';
import { LanguageService } from 'src/app/services/language.service';
import { LoadingService } from 'src/app/services/loading.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'page-login-rescue-password',
  templateUrl: 'login-rescue-password.html',
  styleUrls: ['login-rescue-password.scss'],
  standalone: false
})
export class LoginRescuePasswordPage implements OnInit {
  public sendRescuePassword: UntypedFormGroup;
  public formEmpty: boolean;
  private defaultIntroStyleConfig = {
    logo: '',
    bgColor: '',
    bgImage: '',
    welcomeMessage: ''
  };
  public introStyleConfig: any;
  public logo: any;
  public userData: any;
  get loginPage() {
    return Environment.platform === 'web' ? 'login/web' : 'login/first';
  }

  public get rescueTitleKey(): string {
    return this.initialConfig.defaultTransactionalChannel === 'sms' ? 'LOGIN-RESCUE-MAIL-TITLE-SMS' : 'LOGIN-RESCUE-MAIL-TITLE';
  }

  constructor(
    public router: Router,
    private fb: UntypedFormBuilder,
    public initialConfig: InitialConfigService,
    public loginProvider: LoginService,
    private toast: ToastService,
    private languageService: LanguageService,
    private loading: LoadingService
  ) {
    this.userData = this.router.getCurrentNavigation()?.extras?.state?.userData;
  }

  ngOnInit() {
    this.introStyleConfig = this.initialConfig.styleSheet ? this.initialConfig.styleSheet.intro : this.defaultIntroStyleConfig;
    this.logo = this.introStyleConfig.logo;
    this.createForm();
  }

  createForm() {
    let username = '';
    if (this.userData && this.userData.username) {
      username = this.userData.username;
    }
    this.sendRescuePassword = this.fb.group({ username: [username, Validators.required] });
  }

  public async onSubmit() {
    if (!this.sendRescuePassword.valid) {
      return;
    }

    const username = this.sendRescuePassword.get('username').value;
    const knownClientId = this.resolveKnownClientId();

    if (knownClientId !== null) {
      this.requestCode(knownClientId, username);
      return;
    }

    // Multi-tenant / three-step: clientId is unknown until we resolve clients by username
    await this.loading.show();
    this.loginProvider
      .loginStepOne(username)
      .pipe(finalize(() => this.loading.hide()))
      .subscribe({
        next: (clients) => this.handleClientsForRescue(clients, username),
        error: async (error) => this.handleRescuePasswordError(error)
      });
  }

  private resolveKnownClientId(): number | null {
    if (Environment.CLIENT_ID !== null && Environment.CLIENT_ID !== undefined) {
      return Number(Environment.CLIENT_ID);
    }

    const fromState = this.userData?.id ?? this.userData?.clientId;
    if (fromState !== null && fromState !== undefined && fromState !== '') {
      const parsed = Number(fromState);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  private async handleClientsForRescue(clients: any[], username: string) {
    if (!clients?.length) {
      this.toast.danger(await this.languageService.translate('USER_NOT_FOUND_OR_NO_CHANNEL'));
      return;
    }

    if (clients.length === 1) {
      this.requestCode(Number(clients[0].id), username);
      return;
    }

    this.router.navigate(['login/second/'], {
      state: { data: clients, username, returnToRescue: true }
    });
  }

  private requestCode(clientId: number, username: string) {
    this.loginProvider.requestRecoverPasswordCode(clientId, username).subscribe({
      next: (response) => this.goToRescueCode(username, clientId, response?.channel),
      error: async (error) => this.handleRescuePasswordError(error)
    });
  }

  private async handleRescuePasswordError(error: any) {
    if (error?.status === 404) {
      this.toast.danger(await this.languageService.translate('USER_NOT_FOUND_OR_NO_CHANNEL'));
      return;
    }

    const code: string = error?.error?.code;
    if (code) {
      this.toast.danger(await this.languageService.translate(code.toUpperCase()));
      return;
    }

    this.toast.danger(await this.languageService.translate('ERRORS-GENERIC_ERROR'));
  }

  public goLoginStepOne() {
    this.router.navigate([this.loginPage]);
  }

  private goToRescueCode(username: string, clientId: number, channel: string) {
    this.router.navigate(['rescue/code'], {
      state: {
        username,
        clientId,
        channel,
        userData: {
          ...(this.userData || {}),
          username,
          id: clientId,
          clientId
        }
      }
    });
  }
}
